import React, { Suspense, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectLoginState } from './js/slices/login.slice';
import { selectRegistrationState } from './js/slices/registration.slice';
import { WclErrorBoundary } from './js/shared/WclErrorBoundary';
import { FallBack } from './js/shared/loading-fallback/loading-fallback';
import { Route, Switch, useHistory, useLocation, Redirect } from 'react-router-dom';
import { HOME_PATH, FAQ_PATH, LOGIN_PATH, WAYTO_FAQ_PATH, SIGN_OUT_PATH } from './js/react-router-paths';
import { AuthContainer } from './js/feature-auth/AuthContainer';
import { UpcFaqs } from './js/feature-support/upc-faqs';
import { useGetSsoUrlQuery } from './js/slices/account-api.slice';
import { AccountContainer } from './js/feature-account/account-container';
import { skipToken } from '@reduxjs/toolkit/query';
import { SignOut } from './js/feature-sign-out/SignOut';
import { WaytoFaqs } from './js/feature-support/wayto-faqs';
import { WclMessageContainer } from './WclMessageContainer';
import { LoggingProvider } from '@workday/upc-common';
import { logger } from './js/logger';

const protectedRedirectKey = 'protectedRedirect';

function ProtectedRoute({ children, isLoggedIn, ...rest }) {
    // If the user accesses this route but is not logged in, we persist the path they accessed for later use

    const location = useLocation();
    const savedRedirectRef = useRef(isLoggedIn);

    useEffect(() => {
        if (!savedRedirectRef.current) {
            const redirectPath = location.pathname + location.hash + location.search;
            sessionStorage.setItem(protectedRedirectKey, redirectPath);
            savedRedirectRef.current = true;
        }
    }, [location]);

    return isLoggedIn ? <Route {...rest}>{children}</Route> : <Redirect to={LOGIN_PATH} />;
}

export function App() {
    const history = useHistory();

    const loginState = useSelector(selectLoginState);

    const registrationState = useSelector(selectRegistrationState);

    const ssoQuery = useGetSsoUrlQuery(loginState.application || registrationState.application || skipToken);

    const spRedirectUri = ssoQuery.data?.redirectUri;

    useEffect(() => {
        if (loginState.status === 'LOGGED_IN') {
            const loginRedirect = sessionStorage.getItem(protectedRedirectKey);
            sessionStorage.removeItem(protectedRedirectKey);
            if (loginRedirect) {
                // the user tried to access a protected route
                history.replace(loginRedirect);
            }
        }

        if (loginState.status === 'LOGGED_OUT_WITH_NOTICE') {
            history.replace(SIGN_OUT_PATH);
        }
    }, [history, loginState.status]);

    useEffect(() => {
        if (spRedirectUri) {
            // we have a redirect path from sp login
            // nb. this uses window.location, a new page is loaded here
            window.location.replace(spRedirectUri);
        }
    }, [spRedirectUri]);

    // see useEffect with spRedirectUri above - window.location is going to change at this point, so show a fallback
    const isDoingSpRedirect = loginState.status === 'LOGGED_IN' && (ssoQuery.isLoading || ssoQuery.isSuccess);

    return (
        <LoggingProvider logger={logger}>
            <WclErrorBoundary>
                <Suspense fallback={<FallBack />}>
                    <Switch>
                        <Route path={FAQ_PATH}>
                            <UpcFaqs />
                        </Route>
                        <Route path={WAYTO_FAQ_PATH}>
                            <WaytoFaqs />
                        </Route>
                        <ProtectedRoute path={HOME_PATH} isLoggedIn={loginState.status === 'LOGGED_IN'}>
                            <AccountContainer />
                        </ProtectedRoute>
                        {isDoingSpRedirect ? (
                            <FallBack /> // display fallback on sp flow
                        ) : loginState.status === 'LOGGED_IN' ? (
                            <Redirect to={HOME_PATH} />
                        ) : loginState.status === 'LOGGED_OUT_WITH_NOTICE' ? (
                            <Route path={SIGN_OUT_PATH}>
                                <SignOut />
                            </Route>
                        ) : (
                            <Route path='/'>
                                <AuthContainer />
                            </Route>
                        )}
                    </Switch>
                </Suspense>
                <WclMessageContainer />
            </WclErrorBoundary>
        </LoggingProvider>
    );
}
